import { supabase } from '../supabase';
import { noteNearby } from './nearbyTiming';

// ─── Proximity on the server (open-access lockdown, Step 6a) ──────────────────
// Each phone sends only its OWN position (and GPS accuracy + when its QR page
// opened) to proximity_ping every few seconds while the QR page is open. The
// server matches within 50 m at exact positions and answers with the people
// nearby in rank order — name, photo and id only, never anyone's coordinates
// or distance (Mark, 2026-10-07). Connect / Not now go through
// proximity_signal under this person's own id; the mutual match is decided on
// the server, so same-second taps both land "matched".
// Contract: docs/current/open-access-lockdown.md § Execution log.
//
// The old phone-to-phone area channels (proximity:<geohash>) are retired:
// anyone could listen on any area and hear exact positions.

// Check-in rhythm (signup-proximity-reliability, Mark 2026-10-09: 2–6 s to
// appear "may make it not so magical"). Two people opening together each
// miss the other on their first check-in, so the first seconds — when nearly
// every match happens — check in fast, then about every second. The server
// lists only phones heard from in the last 3 s (044, Mark 2026-10-11: a
// locked phone must never appear), so a phone that is looking must keep
// checking in well inside that. The server stores at most one position per
// 0.5 s (044).
const FAST_PING_MS = 700;
const FAST_WINDOW_MS = 15000;
const PING_INTERVAL_MS = 1000;
const MOVEMENT_THRESHOLD_M = 20; // a real move pings at once instead of waiting

/** Delay before the next check-in, by time since the QR screen started looking. */
export const nextPingDelay = (elapsedMs) =>
  elapsedMs < FAST_WINDOW_MS ? FAST_PING_MS : PING_INTERVAL_MS;

let active = false;
let me = null; // { profileUserId, qrOpenedAt }
let callbacks = {};
let pingTimer = null; // the next check-in
let startedAt = 0;
let onVisible = null;
let paused = false; // locked / backgrounded: gone from the server until back
// The check-in being waited on (one at a time). A lock, a leave or the 2 s
// give-up lets it go: a check-in frozen by the lock or stuck on a dead network
// neither blocks the next one nor delivers its stale answer (Mark's phone
// passes 2026-10-12/13: "3 seconds is too long. It feels broken.").
const CHECK_IN_GIVE_UP_MS = 2000;
let current = null;
let backPending = false; // woke: the first answered check-in is "nearby_back"
let failedSinceWake = 0; // check-ins that failed or gave up before that
let watchId = null;
let watchDelayId = null;
let lastLat = null;
let lastLng = null;
let lastAccuracy = null;
const dismissedPeers = new Set(); // cleared on leave — re-enforced by proactive decline
const proactivelyDeclined = new Set(); // dismissed peers already told this visit

// ─── Haversine Distance (local movement check only) ──────────────────────────

const RADIUS_EARTH_M = 6_371_000;
const toRad = (deg) => (deg * Math.PI) / 180;

function haversineDistance(lat1, lng1, lat2, lng2) {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return RADIUS_EARTH_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ─── The server answer ───────────────────────────────────────────────────────

function handleSignals(signals) {
  for (const s of signals || []) {
    if (s.kind === 'match') {
      console.log('[PROXIMITY] Mutual match (server) with', s.from);
      callbacks.onMutualMatch?.(s.from);
    } else if (s.kind === 'decline') {
      console.log(`[PROXIMITY] Decline from ${s.from}${s.isProactive ? ' (proactive)' : ''}`);
      callbacks.onDeclineReceived?.(s.from, s.isProactive);
    } else if (s.kind === 'invite') {
      if (dismissedPeers.has(s.from)) {
        sendProximityDecline(me?.profileUserId, s.from, true);
      } else {
        // Double-blind: the server holds their tap; it becomes a match the
        // moment this person taps Connect too. Nothing to show yet.
        console.log(`[PROXIMITY] Invite from ${s.from} — held by the server`);
      }
    }
  }
}

function handleNearby(nearby) {
  const users = [];
  for (const u of nearby || []) {
    if (dismissedPeers.has(u.profileUserId)) {
      // They still have a card for us; tell them once so it goes away
      // without permanently re-dismissing (isProactive).
      if (!proactivelyDeclined.has(u.profileUserId)) {
        proactivelyDeclined.add(u.profileUserId);
        sendProximityDecline(me?.profileUserId, u.profileUserId, true);
      }
      continue;
    }
    users.push({
      profileUserId: u.profileUserId,
      name: u.name,
      photoUrl: u.photoUrl,
      rank: u.rank,
    });
  }
  callbacks.onNearbyUsersChanged?.(users); // already in the server's rank order
}

/** Stop waiting on a check-in (the next one goes on the usual rhythm). */
function letGo(checkIn) {
  if (!checkIn || current !== checkIn) return;
  current = null;
  checkIn.controller.abort();
}

async function ping() {
  if (!active || paused || current || lastLat == null || lastLng == null) return;
  const checkIn = { controller: new AbortController() };
  current = checkIn;
  const giveUp = setTimeout(() => {
    if (current !== checkIn) return;
    if (backPending) failedSinceWake += 1;
    console.warn('[PROXIMITY] check-in gave up after 2 s');
    letGo(checkIn);
  }, CHECK_IN_GIVE_UP_MS);
  try {
    const { data, error } = await supabase
      .rpc('proximity_ping', {
        p_profile_user_id: me.profileUserId,
        p_lat: lastLat,
        p_lng: lastLng,
        p_accuracy: lastAccuracy,
        p_qr_opened_at: me.qrOpenedAt,
      })
      .abortSignal(checkIn.controller.signal);
    if (!active || current !== checkIn) return; // let go: a newer check-in owns the answer
    if (error) {
      if (backPending) failedSinceWake += 1;
      console.warn('[PROXIMITY] ping failed:', error.message);
      return;
    }
    if (backPending) {
      backPending = false;
      noteNearby('nearby_back', undefined, { failed: failedSinceWake });
    }
    handleSignals(data?.signals);
    handleNearby(data?.nearby);
    // 055: who my live request is to (null once matched, cancelled or gone)
    callbacks.onWaitingOn?.(data?.waitingOn ?? null);
  } finally {
    clearTimeout(giveUp);
    if (current === checkIn) current = null;
  }
}

// ─── Check-in schedule, pause / resume ───────────────────────────────────────

function schedulePing() {
  clearTimeout(pingTimer);
  pingTimer = setTimeout(
    () => {
      ping();
      if (active && !paused) schedulePing();
    },
    nextPingDelay(Date.now() - startedAt)
  );
}

function sendLeave() {
  if (!me?.profileUserId) return;
  supabase
    .rpc('proximity_leave', { p_profile_user_id: me.profileUserId })
    .then(({ error }) => error && console.warn('[PROXIMITY] leave failed:', error.message));
}

/** Start (or restart) looking: check in at once, then on the fast rhythm. */
function startLooking() {
  paused = false;
  startedAt = Date.now();
  ping();
  schedulePing();
}

/** Stop checking in: no next check-in, and let go of the one in flight. */
function stopCheckingIn() {
  clearTimeout(pingTimer);
  pingTimer = null;
  letGo(current);
  backPending = false;
}

function pauseProximity() {
  if (!active || paused) return;
  paused = true;
  stopCheckingIn();
  sendLeave();
  console.log('[PROXIMITY] Hidden — left the server until back');
}

function resumeProximity() {
  if (!active || !paused) return;
  noteNearby('nearby_woke');
  backPending = true;
  failedSinceWake = 0;
  startLooking(); // a fresh look: the fast rhythm again
  console.log('[PROXIMITY] Back — checking in again');
}

// ─── Join ────────────────────────────────────────────────────────────────────

/**
 * Start proximity for this person. `name`/`photoUrl` are no longer sent — the
 * server shows the profile's own name and photo. Returns the cleanup function.
 * @param {{profileUserId: string, lat: number, lng: number, accuracy?: number, qrOpenedAt?: string}} params
 * @param {{onNearbyUsersChanged?: Function, onMutualMatch?: Function, onDeclineReceived?: Function}} cbs
 */
export function joinProximityChannel(params, cbs) {
  if (active) leaveProximityChannel();
  dismissedPeers.clear();
  proactivelyDeclined.clear();

  const { profileUserId, lat, lng, accuracy = null, qrOpenedAt } = params;
  me = { profileUserId, qrOpenedAt: qrOpenedAt || new Date().toISOString() };
  callbacks = cbs || {};
  lastLat = lat;
  lastLng = lng;
  lastAccuracy = accuracy;
  active = true;
  startLooking();
  // "Both phones need to be active and looking at this screen for a connection
  // to happen" (Mark, 2026-10-11): locked or backgrounded → gone from the
  // server at once (its pending Connects go too); back in front → check in at
  // once on the fast rhythm. The server also drops a phone it hasn't heard
  // from in 3 s, for a goodbye that arrives late or never (iOS can hold
  // requests the moment it locks).
  onVisible = () => {
    if (document.visibilityState === 'hidden') pauseProximity();
    else resumeProximity();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pagehide', pauseProximity);

  // GPS watch — deferred 1s so the user-gesture-initiated getCurrentPosition()
  // establishes permission first (avoids the browser violation warning)
  watchDelayId = setTimeout(() => {
    if (!navigator.geolocation) return;
    watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy: acc } = position.coords;
        const moved = haversineDistance(lastLat, lastLng, latitude, longitude);
        lastLat = latitude;
        lastLng = longitude;
        lastAccuracy = acc ?? null;
        if (moved > MOVEMENT_THRESHOLD_M) ping();
      },
      (err) => {
        console.warn('[PROXIMITY] watchPosition error:', err.message);
      },
      { enableHighAccuracy: true, maximumAge: 10000 }
    );
  }, 1000);

  return () => leaveProximityChannel();
}

// ─── Connect (invite) ────────────────────────────────────────────────────────
// The server answers 'matched' when the other person already tapped Connect
// for us; they receive a 'match' signal on their next ping.

// Resolves to the server's answer — 'sent' · 'matched' · 'not_nearby' · 'busy'
// (055: either of us is waiting on someone else, or mid-exchange) — or null.
// `held`: the new code page's request, which holds this person until it ends
// (R1/R2); the old screens' Connect keeps 053's rules (055 step 12.13).
export function sendProximityInvite(senderProfileUserId, target, { held = false } = {}) {
  return supabase
    .rpc('proximity_signal', {
      p_from: senderProfileUserId,
      p_to: target,
      p_kind: held ? 'invite_held' : 'invite',
    })
    .then(({ data, error }) => {
      if (error) {
        console.warn('[PROXIMITY] invite failed:', error.message);
        return null;
      }
      console.log(`[PROXIMITY] Invite to ${target}: ${data}`);
      if (data === 'matched' && active) {
        callbacks.onMutualMatch?.(target);
      }
      return data;
    });
}

// ─── Withdraw (the waiting X, 055) ───────────────────────────────────────────
// Takes my own request back without telling them (a decline would dismiss me
// from their screen) — I'm free again at once (F5, R2). Resolves to
// 'withdrawn' · 'gone' (there was no request to take back) · null.

export function sendProximityWithdraw(senderProfileUserId, target) {
  if (!senderProfileUserId || !target) return Promise.resolve(null);
  return supabase
    .rpc('proximity_signal', { p_from: senderProfileUserId, p_to: target, p_kind: 'withdraw' })
    .then(({ data, error }) => {
      if (error) console.warn('[PROXIMITY] withdraw failed:', error.message);
      return error ? null : data;
    });
}

// ─── Not now (decline) ───────────────────────────────────────────────────────

export function sendProximityDecline(
  senderProfileUserId,
  targetProfileUserId,
  isProactive = false
) {
  if (!senderProfileUserId || !targetProfileUserId) return;
  supabase
    .rpc('proximity_signal', {
      p_from: senderProfileUserId,
      p_to: targetProfileUserId,
      p_kind: 'decline',
      p_is_proactive: isProactive,
    })
    .then(({ error }) => {
      if (error) console.warn('[PROXIMITY] decline failed:', error.message);
    });
}

// ─── Dismiss tracking ────────────────────────────────────────────────────────

export function addDismissedPeer(profileUserId) {
  dismissedPeers.add(profileUserId);
}

export function isProximityChannelActive() {
  return active;
}

// ─── Leave ───────────────────────────────────────────────────────────────────

export function leaveProximityChannel() {
  stopCheckingIn();
  if (onVisible) {
    document.removeEventListener('visibilitychange', onVisible);
    onVisible = null;
  }
  window.removeEventListener('pagehide', pauseProximity);
  if (watchDelayId != null) {
    clearTimeout(watchDelayId);
    watchDelayId = null;
  }
  if (watchId != null) {
    navigator.geolocation.clearWatch(watchId);
    watchId = null;
  }
  if (active && !paused) sendLeave();
  active = false;
  paused = false;
  me = null;
  callbacks = {};
  lastLat = null;
  lastLng = null;
  lastAccuracy = null;
  dismissedPeers.clear(); // navigating away resets your dismissed set
  proactivelyDeclined.clear();
  console.log('[PROXIMITY] Left proximity, state reset');
}
